/**
 * useIssueAiAnalysis — Debounced AI analysis of issue descriptions.
 *
 * Fires after 800ms of inactivity when description >= 10 chars.
 * Only active in create mode (isCreating = true).
 * Uses a ref for getToken to avoid dependency churn.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { aiApi, type AISuggestion } from '../../../lib/api';
import type { IssueSeverity } from '@overlayqa/shared';
import { log } from '@/lib/logger';

log.info('[useIssueAiAnalysis] Module loaded');

const AI_DEBOUNCE_MS = 800;
const AI_MIN_CHARS = 10;

export interface UseIssueAiAnalysisOptions {
  description: string;
  isCreating: boolean;
  isOpen: boolean;
  getToken: () => Promise<string | null>;
}

export interface UseIssueAiAnalysisReturn {
  suggestion: AISuggestion | null;
  isAnalyzing: boolean;
  aiError: string | null;
  applySuggestions: () => {
    title: string;
    description?: string;
    severity: IssueSeverity;
  } | null;
  resetAiState: () => void;
}

export function useIssueAiAnalysis({
  description,
  isCreating,
  isOpen,
  getToken,
}: UseIssueAiAnalysisOptions): UseIssueAiAnalysisReturn {
  log.info('[useIssueAiAnalysis] Hook called', { isCreating, descLen: description.length });

  const [suggestion, setSuggestion] = useState<AISuggestion | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const analysisTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Store getToken in a ref to avoid dependency issues
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // Reset AI state when drawer opens
  useEffect(() => {
    if (isOpen) {
      log.info('[useIssueAiAnalysis] Drawer opened — resetting AI state');
      setSuggestion(null);
      setIsAnalyzing(false);
      setAiError(null);
    }
  }, [isOpen]);

  // Debounced AI analysis (only for new issues)
  useEffect(() => {
    if (!isCreating) return;

    if (analysisTimeoutRef.current) {
      clearTimeout(analysisTimeoutRef.current);
    }

    if (description.length >= AI_MIN_CHARS) {
      log.info('[useIssueAiAnalysis] Starting debounce timer for AI analysis');
      setIsAnalyzing(true);
      setAiError(null);
      analysisTimeoutRef.current = setTimeout(async () => {
        try {
          log.info('[useIssueAiAnalysis] Debounce fired — calling AI API');
          const token = await getTokenRef.current();
          const result = await aiApi.analyzeIssue(token ?? undefined, description);
          log.info('[useIssueAiAnalysis] AI result received, confidence:', { confidence: result.confidence });
          setSuggestion(result);
          setAiError(null);
        } catch (error) {
          log.error('[useIssueAiAnalysis] AI analysis failed:', { error });
          setAiError('AI analysis unavailable');
          setSuggestion(null);
        } finally {
          setIsAnalyzing(false);
        }
      }, AI_DEBOUNCE_MS);
    } else {
      setSuggestion(null);
      setIsAnalyzing(false);
    }

    return () => {
      if (analysisTimeoutRef.current) {
        clearTimeout(analysisTimeoutRef.current);
      }
    };
  }, [description, isCreating]);

  const applySuggestions = useCallback(() => {
    if (!suggestion) {
      log.info('[useIssueAiAnalysis] applySuggestions called but no suggestion available');
      return null;
    }
    log.info('[useIssueAiAnalysis] Applying suggestions:', { title: suggestion.title });
    return {
      title: suggestion.title,
      description: suggestion.description || undefined,
      severity: suggestion.severity as IssueSeverity,
    };
  }, [suggestion]);

  const resetAiState = useCallback(() => {
    log.info('[useIssueAiAnalysis] Resetting AI state');
    setSuggestion(null);
    setIsAnalyzing(false);
    setAiError(null);
  }, []);

  return {
    suggestion,
    isAnalyzing,
    aiError,
    applySuggestions,
    resetAiState,
  };
}
